import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Phone, Mail, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useApi } from "@/lib/api";
import { PRODUCTS, STATUSES, productOf, statusLabel, fmtDateTime, urgency } from "@/lib/crm";
import { ClientFormDialog } from "@/components/ClientFormDialog";
import { ImportDialog } from "@/components/ImportDialog";
import { ConsentBadge } from "@/components/ConsentBadge";

const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{children}</button>
);

const STATUS_CLS = { lead: "bg-sky-100 text-sky-800", cliente: "bg-emerald-100 text-emerald-800", ex_cliente: "bg-slate-100 text-slate-600" };

export default function Clients() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [product, setProduct] = useState("");
  const [add, setAdd] = useState(false);
  const [imp, setImp] = useState(false);
  const navigate = useNavigate();
  const { data = [], isLoading } = useApi(`/clients?q=${encodeURIComponent(q)}&status=${status}&product=${product}`);

  return (
    <div className="space-y-6 fade-up" data-testid="clients-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Clienti e nominativi</h1>
          <p className="text-slate-500 mt-1">{data.length} nominativi</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="import-clients-btn" onClick={() => setImp(true)} className="flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium hover:border-sky-500 hover:text-sky-700 transition-colors"><Upload className="h-4 w-4" />Importa da Excel</button>
          <button data-testid="add-client-btn" onClick={() => setAdd(true)} className="flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-sky-600 transition-colors"><Plus className="h-4 w-4" />Nuovo nominativo</button>
        </div>
      </div>
      <ImportDialog open={imp} onOpenChange={setImp} />

      <div className="space-y-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, telefono, email, codice fiscale…" className="pl-9 bg-white" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip testid="filter-status-all" active={!status} onClick={() => setStatus("")}>Tutti</Chip>
          {STATUSES.map((s) => <Chip key={s.id} testid={`filter-status-${s.id}`} active={status === s.id} onClick={() => setStatus(s.id)}>{s.label}</Chip>)}
          <span className="w-px bg-slate-200 mx-1" />
          {PRODUCTS.map((p) => <Chip key={p.id} testid={`filter-product-${p.id}`} active={product === p.id} onClick={() => setProduct(product === p.id ? "" : p.id)}>{p.label}</Chip>)}
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="px-5 py-3 font-medium">Nominativo</th><th className="px-5 py-3 font-medium">Contatti</th>
              <th className="px-5 py-3 font-medium">Interessi</th><th className="px-5 py-3 font-medium">Stato</th><th className="px-5 py-3 font-medium">Marketing</th>
              <th className="px-5 py-3 font-medium">Prossimo richiamo</th>
            </tr>
          </thead>
          <tbody>
            {data.map((c) => (
              <tr key={c.client_id} data-testid={`client-row-${c.client_id}`} onClick={() => navigate(`/clienti/${c.client_id}`)} className="border-b border-slate-100 last:border-0 hover:bg-sky-50/40 cursor-pointer transition-colors">
                <td className="px-5 py-3.5"><p className="font-medium text-slate-900">{c.first_name} {c.last_name}</p><p className="text-xs text-slate-400">{c.city || c.job}</p></td>
                <td className="px-5 py-3.5 text-slate-600">
                  {c.phone && <p className="flex items-center gap-1.5 font-mono text-xs"><Phone className="h-3 w-3" />{c.phone}</p>}
                  {c.email && <p className="flex items-center gap-1.5 text-xs mt-0.5"><Mail className="h-3 w-3" />{c.email}</p>}
                </td>
                <td className="px-5 py-3.5"><div className="flex flex-wrap gap-1">{c.interests.map((i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full text-white" style={{ background: productOf(i).color }}>{productOf(i).label}</span>)}</div></td>
                <td className="px-5 py-3.5"><span className={`text-xs px-2 py-1 rounded-md ${STATUS_CLS[c.status]}`}>{statusLabel(c.status)}</span></td>
                <td className="px-5 py-3.5"><ConsentBadge client={c} testid={`client-consent-${c.client_id}`} /></td>
                <td className="px-5 py-3.5 font-mono text-xs">
                  {c.next_reminder ? <span className={urgency(c.next_reminder) === "scaduto" ? "text-rose-600 font-semibold" : urgency(c.next_reminder) === "oggi" ? "text-amber-600 font-semibold" : "text-slate-600"}>{fmtDateTime(c.next_reminder)}</span> : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && data.length === 0 && <p className="text-center text-slate-400 py-14" data-testid="clients-empty">Nessun nominativo trovato. Aggiungi il primo!</p>}
      </div>
      <ClientFormDialog open={add} onOpenChange={setAdd} onSaved={(c) => navigate(`/clienti/${c.client_id}`)} />
    </div>
  );
}
